// Mark steps done previously (spec C8, the Record's "..." menu; S16): for renewals worked before the
// portal existed. Steps 1 to 5 with checkboxes, who did them (the person marking; the Manager and the
// Admin can name a Renewal VA) and the day, typed. Done stays disabled until every step before the
// last one picked is done or picked too. It never closes the renewal. Markup from design/kit (Dialog,
// Checkbox, Dropdown, Input).
import { useEffect, useState } from 'react';
import type { Api, Assignee, PreviouslyStep, RenewalDetail, StepsView } from '../auth/api';
import { Dialog } from '../shell/Dialog';
import { CheckIcon, ChevronDownIcon } from '../shell/icons';
import { nyToday } from './renewal-lines';
import {
  previouslyDate,
  previouslyReady,
  previouslyRows,
  refusalText,
  type PreviouslyRow,
} from './step-lines';

/** Who is marking: the signed-in person, and whether they may name a Renewal VA. */
export type Marker = { id: string; name: string; manages: boolean };

export function StepsPreviouslyDialog({
  api,
  view,
  renewal,
  me,
  onClose,
  onSaved,
}: {
  api: Api;
  view: StepsView;
  renewal: RenewalDetail;
  me: Marker;
  onClose: () => void;
  onSaved: (result: { batch: string; steps: StepsView; numbers: number[] }) => void;
}) {
  const rows = previouslyRows(view);
  const [picked, setPicked] = useState<ReadonlySet<PreviouslyStep>>(new Set());
  const [who, setWho] = useState(me.id);
  const [vas, setVas] = useState<Assignee[] | null>(me.manages ? null : []);
  const [date, setDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The Manager and the Admin can name a Renewal VA: the same list Reassign uses.
  useEffect(() => {
    if (!me.manages) return;
    let live = true;
    api.assignees().then(
      (a) => live && setVas(a.people.filter((p) => p.role === 'renewal_va')),
      () => live && setVas([]),
    );
    return () => {
      live = false;
    };
  }, [api, me.manages]);

  const day = previouslyDate(date, nyToday(new Date()));
  const people = [{ id: me.id, name: me.name }, ...(vas ?? []).filter((p) => p.id !== me.id)];
  const ready = previouslyReady(rows, picked) && !!day && 'value' in day && !busy;

  async function save() {
    if (!day || !('value' in day)) return;
    setBusy(true);
    setError(null);
    const steps = rows.filter((r) => picked.has(r.key)).map((r) => r.key);
    const res = await api.markStepsPreviously(renewal.id, {
      steps,
      doneBy: who,
      date: day.value,
    });
    setBusy(false);
    if (!res.ok) {
      setError(refusalText(res.error));
      return;
    }
    onSaved({
      ...res.body,
      numbers: rows.filter((r) => picked.has(r.key)).map((r) => r.n),
    });
  }

  return (
    <StepsPreviouslyView
      subline={[renewal.insured, renewal.carrier ?? 'Carrier', renewal.line, renewal.policyNumber]
        .filter(Boolean)
        .join(' | ')}
      rows={rows}
      picked={picked}
      onToggle={(k) => {
        const next = new Set(picked);
        if (next.has(k)) next.delete(k);
        else next.add(k);
        setPicked(next);
      }}
      people={me.manages ? people : null}
      peopleLoading={me.manages && vas === null}
      who={who}
      whoName={people.find((p) => p.id === who)?.name ?? me.name}
      onWho={setWho}
      date={date}
      dateError={day && 'error' in day ? day.error : null}
      onDate={setDate}
      ready={ready}
      gap={picked.size > 0 && !previouslyReady(rows, picked)}
      error={error}
      onSave={() => void save()}
      onClose={onClose}
    />
  );
}

/** The dialog itself, from plain values, so a browser test can render it with the kit's CSS. */
export function StepsPreviouslyView({
  subline,
  rows,
  picked,
  onToggle,
  people,
  peopleLoading,
  who,
  whoName,
  onWho,
  date,
  dateError,
  onDate,
  ready,
  gap,
  error,
  onSave,
  onClose,
}: {
  subline: string;
  rows: PreviouslyRow[];
  picked: ReadonlySet<PreviouslyStep>;
  onToggle: (k: PreviouslyStep) => void;
  /** The people the Manager can name; null when the person marking is the only choice. */
  people: { id: string; name: string }[] | null;
  peopleLoading: boolean;
  who: string;
  whoName: string;
  onWho: (id: string) => void;
  date: string;
  dateError: string | null;
  onDate: (v: string) => void;
  ready: boolean;
  /** A step is picked while one before it is neither done nor picked. */
  gap: boolean;
  error: string | null;
  onSave: () => void;
  onClose: () => void;
}) {
  const [listOpen, setListOpen] = useState(false);
  return (
    <Dialog
      id="prev-t"
      title="Mark steps done previously"
      subline={subline}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={ready ? 'btn btn-primary ml-auto' : 'btn ml-auto'}
            disabled={!ready}
            onClick={onSave}
          >
            Done
          </button>
        </>
      }
    >
      <div>
        <div className="flab" id="prev-steps">
          Steps done before the portal <span className="req">*</span>
        </div>
        <div className="sumlist" role="group" aria-labelledby="prev-steps">
          {rows.map((r) => {
            const settled = r.done !== null;
            return (
              <div key={r.key} className="sumrow">
                <label className="ckline grow">
                  <input
                    type="checkbox"
                    checked={settled || picked.has(r.key)}
                    disabled={settled || r.why !== null}
                    onChange={() => onToggle(r.key)}
                  />
                  {r.n}. {r.title}
                </label>
                {(r.done ?? r.why) && <span className="t12 muted ell">{r.done ?? r.why}</span>}
              </div>
            );
          })}
        </div>
        {gap ? (
          <p className="fnote">Pick every step before the last one you pick, or tick it first.</p>
        ) : (
          <p className="fnote">
            They show as done previously, with who and the day. This never closes the renewal.
          </p>
        )}
      </div>
      <div>
        <span className="flab" id="prev-who-l">
          Who did them <span className="req">*</span>
        </span>
        {people ? (
          <div className="sel">
            <button
              type="button"
              className="sel-b"
              id="prev-who"
              aria-haspopup="listbox"
              aria-expanded={listOpen}
              aria-labelledby="prev-who-l prev-who"
              disabled={peopleLoading}
              onClick={() => setListOpen(!listOpen)}
            >
              <span className="sel-v">{peopleLoading ? 'Loading' : whoName}</span>
              <ChevronDownIcon className="ico s12" />
            </button>
            {listOpen && (
              <div className="menu sel-m" role="listbox" aria-label="Who did them">
                {people.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="mi"
                    role="option"
                    aria-selected={who === p.id}
                    onClick={() => {
                      onWho(p.id);
                      setListOpen(false);
                    }}
                  >
                    <span className="dd-ck">
                      {who === p.id && <CheckIcon className="ico s12" />}
                    </span>
                    <span>{p.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <p className="m0 t13 ink" aria-labelledby="prev-who-l">
            {whoName}
          </p>
        )}
      </div>
      <div>
        <label htmlFor="prev-date" className="flab">
          Date done <span className="req">*</span>
        </label>
        <input
          id="prev-date"
          className="input"
          placeholder="Oct 30, 2026"
          autoComplete="off"
          value={date}
          aria-invalid={!!dateError}
          onChange={(e) => onDate(e.target.value)}
        />
        {dateError && <p className="errl">{dateError}</p>}
      </div>
      {error && <p className="ferr">{error}</p>}
    </Dialog>
  );
}
